import { call, put, select, take, takeEvery } from 'redux-saga/effects';

import { createWatcher } from '@elab/core/store/core/saga';
import { types as authTypes } from '@elab/core/store/auth';
import logger from '@elab/core/utils/logger';
import authCookiesManager from '@elab/core/utils/authCookiesManager';
import { actions as uiActions, selectors as uiSelectors } from '@elab/core/store/ui';
import cartApi from '@elab/core/store/cart/api';
import { actions as globalActions, selectors as globalSelectors } from '@elab/core/store/global';
import {
    actions as cartActions,
    selectors as cartSelectors,
    types as cartTypes,
} from '@elab/core/store/cart';
import { handleUseDeliveryAsBillingSetter } from '@elab/core/store/checkout/saga';
import {
    removeAddressBookSuccessMessage,
    setDefaultAddressBookSuccessMessage,
} from '@elab/core/utils/constants';
import { selectors as configSelectors } from '@elab/core/store/config';
import isEmpty from '@elab/core/utils/isEmpty';
import { setCookie } from '@elab/core/utils/cookie';
import {
    actions as analyticsActions,
    selectors as analyticsSelectors,
} from '@elab/core/store/analytics';

import { getUserId } from '../../utils/user/sagaHelpers';
import { getEGCcartId } from '../cart/saga';

import api from './api';

import { actions, selectors, types } from '.';

function* watchLoadAddresses() {
    yield takeEvery(types.LOAD_ADDRESSES, function*({ payload }) {
        const isAnonymous = yield call(authCookiesManager.isAnonymous);
        if (isAnonymous) {
            return;
        }

        yield put(actions.setLoading(true));

        let restrictAddress = false;
        if (payload && payload.restrictAddress) {
            restrictAddress = true;
        }
        const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);

        try {
            const data = yield call(api.loadAddresses, deliveryCountry, restrictAddress);
            yield put(actions.setAddresses(data));
        } catch (error) {
            /* eslint */
        }

        yield put(actions.setLoading(false));
    });
}

export function* onLoadBillingAddresses() {
    const isAnonymous = yield call(authCookiesManager.isAnonymous);
    if (isAnonymous) {
        return;
    }

    yield put(actions.setLoading(true));

    try {
        const data = yield call(api.loadBillingAddresses);
        yield put(actions.setBillingAddresses(data));
    } catch (error) {
        yield call(logger.error, error);
    }

    yield put(actions.setLoading(false));
}

function* watchLoadBillingAddresses() {
    yield takeEvery(types.LOAD_BILLING_ADDRESSES, onLoadBillingAddresses);
}

export function* isBillingAddressId(id) {
    const billingAddressList = yield select(selectors.getBillingAddresses);
    return billingAddressList.find(address => address.id === id) !== undefined;
}

function* watchRemoveAddresses() {
    yield takeEvery(types.REMOVE_ADDRESS, onRemoveAddresses);
}

export function* onRemoveAddresses({ payload }) {
    const isAnonymous = yield call(authCookiesManager.isAnonymous);
    if (isAnonymous) {
        return;
    }

    let id = payload;
    let isAddressRemovedWithConfirmation;

    if (typeof id === 'object') {
        isAddressRemovedWithConfirmation = true;
        const addressToRemove = yield select(selectors.getAddressToRemove);
        id = addressToRemove && addressToRemove.id;
    }

    yield call(api.removeAddress, id);

    if (isAddressRemovedWithConfirmation) {
        yield put(uiActions.closeDialog());
        yield put(actions.setAddressToRemove(null));
    }

    yield call(updateAddressesAfterRemove, id);
    yield put(globalActions.addMessage(removeAddressBookSuccessMessage));
}

export function* updateAddressesAfterRemove(id) {
    const isBillingAddress = yield call(isBillingAddressId, id);
    const addressList = yield select(
        isBillingAddress ? selectors.getBillingAddresses : selectors.getAddresses,
    );
    const resetSelectedAddressAfterRemove = yield select(
        configSelectors.getResetSelectedAddressAfterRemove,
    );
    const addressesObject = {
        addresses: addressList.filter(address => {
            return address.id !== id;
        }),
    };

    const defaultAddressIsSet = !addressesObject.addresses.some(
        address => address.defaultAddress === true,
    );

    if (defaultAddressIsSet) {
        if (isBillingAddress) {
            yield put(actions.loadBillingAddresses());
        } else {
            yield put(actions.loadAddresses());
        }
    } else {
        if (isBillingAddress) {
            yield put(actions.setBillingAddresses(addressesObject));
        } else {
            yield put(actions.setAddresses(addressesObject));
        }
    }

    if (resetSelectedAddressAfterRemove) {
        const currentAddressId = yield select(
            isBillingAddress
                ? selectors.getCurrentBillingAddressId
                : selectors.getCurrentDeliveryAddressId,
        );
        if (id === currentAddressId) {
            if (isBillingAddress) {
                yield put(actions.setCurrentBillingAddressId(null));
            } else {
                yield put(actions.setCurrentDeliveryAddressId(null));
            }
        }
    }
}

export function* setDefaultAddress(selector, id) {
    const addressList = yield select(selector);
    return {
        addresses: addressList.map(address => ({
            ...address,
            defaultAddress: address.id === id,
        })),
    };
}

export function* setAsDefaultWithoutReloading(payload) {
    const isBillingAddress = yield call(isBillingAddressId, payload.id);

    if (isBillingAddress) {
        const billingAddressesObject = yield call(
            setDefaultAddress,
            selectors.getBillingAddresses,
            payload.id,
        );
        yield put(actions.setBillingAddresses(billingAddressesObject));
    } else {
        const addressesObject = yield call(setDefaultAddress, selectors.getAddresses, payload.id);
        yield put(actions.setAddresses(addressesObject));
    }

    if (!payload.noMessage)
        yield put(globalActions.addMessage(setDefaultAddressBookSuccessMessage));
}

function* watchSetAsDefaultAddress() {
    yield takeEvery(types.SET_AS_DEFAULT, function*({ payload }) {
        yield put(actions.setLoading(true));

        try {
            yield call(api.setAsDefault, payload.id);
            yield call(setAsDefaultWithoutReloading, payload);
        } catch (error) {
            /* eslint */
        }

        yield put(actions.setLoading(false));
    });
}

export function* onOpenAddressBook({ payload }) {
    yield put(actions.setAddressBookType(payload.addressBookType));
    yield put(uiActions.openDialog(payload));
}

function* watchOpenAddressBook() {
    yield takeEvery(types.OPEN_ADDRESS_BOOK, onOpenAddressBook);
}

export function* onCheckAndValidate() {
    let selectedAddressId = yield select(selectors.getSelectedAddressId);
    const addressBookType = yield select(selectors.getAddressBookType);
    const isDelivery = addressBookType === 'delivery';

    if (!selectedAddressId) {
        selectedAddressId = isDelivery
            ? yield select(selectors.getCurrentDeliveryAddressId)
            : yield select(selectors.getCurrentBillingAddressId);
    }

    if (selectedAddressId) {
        const userId = yield call(getUserId);
        const cartId = yield select(cartSelectors.getCartId);
        const guid = yield select(cartSelectors.getGuid);
        const addQueryParamCountry = yield select(configSelectors.getAddQueryParamCountry);

        const api = isDelivery ? cartApi.putDeliveryAddress : cartApi.putBillingAddress;

        const isEgiftCardCartActive = !!(yield call(getEGCcartId));

        const cartIdentifier = isEgiftCardCartActive ? guid : cartId;

        try {
            if (addQueryParamCountry) {
                const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);
                yield call(
                    api,
                    userId,
                    cartIdentifier,
                    selectedAddressId,
                    isEgiftCardCartActive,
                    deliveryCountry,
                );
            } else {
                yield call(api, userId, cartIdentifier, selectedAddressId, isEgiftCardCartActive);
            }
            if (isDelivery) {
                yield put(actions.setCurrentDeliveryAddressId(selectedAddressId));
            } else {
                yield put(actions.setCurrentBillingAddressId(selectedAddressId));
            }
        } catch (e) {
            // nothing
        }

        yield put(cartActions.loadCart());
        yield take(cartTypes.SET_CART_DATA);
        yield call(handleUseDeliveryAsBillingSetter);
        yield put(uiActions.closeDialog());
    } else {
        yield put(actions.setInvalidAddress(true));
    }
}

function* watchCheckAndValidate() {
    yield takeEvery(types.CHECK_AND_VALIDATE, onCheckAndValidate);
}

export function* onResetAddressBookData() {
    yield put(actions.setInvalidAddress(false));
    yield put(actions.setSelectedAddressId());
}

function* watchResetAddressBookData() {
    yield takeEvery(types.RESET_ADDRESS_BOOK_DATA, onResetAddressBookData);
}

export function* onRemoveWithConfirmation(action) {
    const addresses = yield select(selectors.getAddresses);
    const billingAddresses = yield select(selectors.getBillingAddresses);
    const addressToDelete = [...addresses, ...billingAddresses].find(
        address => address.id === action.payload.id,
    );

    yield put(actions.setAddressToRemove(addressToDelete));
    yield put(uiActions.openDialog({ name: 'confirm-address-delete-dialog' }));
}

export function* setAnalyticsCookies() {
    const setAnalyticsCookies = yield select(configSelectors.setAnalyticsCookie);
    if (setAnalyticsCookies) {
        const { width, height } = yield select(uiSelectors.getWindowSize);
        const { sessionId, referrer, location } = yield select(analyticsSelectors.getPageInfo);
        const analyticsData =
            `envScreenSize=${width}x${height}&userSession=${sessionId}&` +
            `referrer=${referrer}&location=${location}`;

        yield call(setCookie, 'analyticsData', analyticsData, false);
    }
}

export function* resetSelectedAddressesAndRedirect({ payload }) {
    try {
        yield put(analyticsActions.setDeliveryOptionCompleted());
        yield call(setAnalyticsCookies);
        yield put(globalActions.redirect(payload.successRedirectUrl));
        yield put(actions.setCurrentBillingAddressId(null));
        yield put(actions.setCurrentDeliveryAddressId(null));
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* setSelectedForPaymentAddresses() {
    const cartData = yield select(cartSelectors.getData);

    if (isEmpty(cartData)) {
        yield take(cartTypes.SET_CART_DATA);
    }

    const paymentAddress = yield select(cartSelectors.getPaymentAddress);
    const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);

    if (!isEmpty(paymentAddress)) {
        yield put(actions.setCurrentBillingAddressId(paymentAddress.id));
    }
    if (!isEmpty(deliveryAddress)) {
        yield put(actions.setCurrentDeliveryAddressId(deliveryAddress.id));
    }
}

export function* setAddressForPayment({ payload }) {
    const { id, isDelivery } = payload;
    const cartId = yield select(cartSelectors.getCartId);
    const userId = yield call(getUserId);

    try {
        yield call(
            isDelivery ? cartApi.putDeliveryAddress : cartApi.putBillingAddress,
            userId,
            cartId,
            id,
        );
        yield put(
            actions[isDelivery ? 'setCurrentDeliveryAddressId' : 'setCurrentBillingAddressId'](id),
        );
    } catch (e) {
        yield call(logger.error, e);
    }

    yield put(cartActions.loadCart());
    yield put(uiActions.closeDialog());
}

export default [
    watchLoadAddresses(),
    watchLoadBillingAddresses(),
    watchSetAsDefaultAddress(),
    watchRemoveAddresses(),
    watchOpenAddressBook(),
    watchCheckAndValidate(),
    watchResetAddressBookData(),
    takeEvery(types.REMOVE_WITH_CONFIRMATION, onRemoveWithConfirmation),
    takeEvery(types.RESET_SELECTED_ADDRESSES_AND_REDIRECT, resetSelectedAddressesAndRedirect),
    takeEvery(types.SET_SELECTED_FOR_PAYMENT_ADDRESSES, setSelectedForPaymentAddresses),
    takeEvery(types.SET_ADDRESS_FOR_PAYMENT, setAddressForPayment),
    createWatcher(authTypes.AFTER_LOGOUT, actions.setInitial()),
];
